import {computed, effect, inject, Injectable, Provider, signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {FormControl, NonNullableFormBuilder, Validators} from '@angular/forms';
import {DateTime} from 'luxon';
import {Router} from '@angular/router';
import {filter, finalize, map, switchMap, take} from 'rxjs';
import {Store} from '@ngrx/store';
import {ToastrService} from 'ngx-toastr';
import {selectUser} from 'src/app/state/selectors';
import {AppValidators, browserTimeZone, TranslateService} from '@brunata-angular/util';
import {meetsBuildingServiceRequirement} from '@shared/route-utils';
import {
    meterValuesReportFileName,
    MeterValuesReportService,
} from '../shared/services/meter-values-report.service';
import {SavedExportsService} from '../saved-exports/saved-exports.service';
import {exportTestProviders} from '../shared/services/export-test.service';
import {sftpConnectionRemovedLimits} from '../steps/step-menu/export-limits.model';
import {
    defaultExportScheduleValue,
    SavedExportColumnConfig,
    savedExportFileName,
    savedExportScheduleFields,
    SavedExportTestRequest,
    maxSavedExportNameLength,
} from '../saved-exports/saved-export.model';
import {
    defaultExportName,
    EXPORT_FORM,
    ExportForm,
    exportScheduleGroup,
    ExportTestSource,
    scheduleFormValue,
    sftpConnectionRemoval,
} from '../shared/models/export-form.model';
import {quickExportModes} from './quick-exports.model';
import {
    defaultFormatForMode,
    formatsForMode,
    QuickExportMode,
} from '../shared/models/output-format.model';
import {
    defaultExportColumns,
    dismountedColumnId,
    exportColumnIdSchema,
} from '../shared/models/export-columns.model';
import {
    bindExportFileFormat,
    defaultExportFileFormat,
    exportFileFormatForm,
    ExportFileFormatForm,
    resetExportFileFormat,
} from '../shared/models/export-file-format.model';
import {FilePreviewContent} from '../file-preview/file-preview.model';
import {previewContent, PreviewShape} from '../file-preview/export-preview.model';
import {FlowSlot} from '../shared/models/export-flow-slots.model';
import {controlStepStatus, StepStatus} from '../steps/step-focus/step-state.model';

const deliveredQuickExportShape: Omit<PreviewShape, 'fileFormat'> = {
    columns: defaultExportColumns
        .filter(column => column.id !== dismountedColumnId)
        .map(({id, header}) => ({id, header})),
    headerRow: 'EVERY_EXPORT',
    unitPlacement: 'OWN_COLUMN',
};

function storedExportShape(stored: SavedExportColumnConfig): Omit<PreviewShape, 'fileFormat'> {
    const columns = stored.columns.flatMap(({columnId, included, header}) => {
        const id = exportColumnIdSchema.safeParse(columnId);
        return included && id.success ? [{id: id.data, header}] : [];
    });
    return {
        columns: columns.length === 0 ? deliveredQuickExportShape.columns : columns,
        headerRow: stored.headerRow,
        unitPlacement: stored.unitPlacement,
    };
}

@Injectable()
export class QuickExportsFormService implements ExportTestSource {
    private readonly meterValuesReportService = inject(MeterValuesReportService);
    private readonly savedExportsService = inject(SavedExportsService);
    private readonly toastrService = inject(ToastrService);
    private readonly translateService = inject(TranslateService);
    private readonly formBuilder = inject(NonNullableFormBuilder);
    private readonly router = inject(Router);
    private readonly store = inject(Store);

    readonly selectedMode = signal<QuickExportMode>('specific-date');
    readonly selectedFormat = signal(defaultFormatForMode('specific-date'));
    readonly storedColumnConfig = signal<SavedExportColumnConfig | null>(null);
    readonly isExporting = signal(false);
    readonly isSaving = signal(false);
    // The edit modal turns this off for on-demand rows, so the schedule half stays disabled (and
    // out of validity) even while the form is on the schedule path.
    readonly scheduleGroupActive = signal(true);

    private readonly buildingServices = toSignal(
        this.store.select(selectUser).pipe(
            filter(state => state.status === 'success'),
            map(state => state.user.buildingServices),
        ),
        {initialValue: []},
    );

    readonly modeOptions = computed(() =>
        quickExportModes.filter(mode =>
            meetsBuildingServiceRequirement(this.buildingServices(), mode.requireBuildingService),
        ),
    );
    readonly formatOptions = computed(() => formatsForMode(this.selectedMode()));

    readonly fileFormat: ExportFileFormatForm = exportFileFormatForm(
        this.formBuilder,
        defaultExportFileFormat,
        () => this.selectedFormat().outputFormat === 'CSV',
    );

    private readonly boundFileFormat = bindExportFileFormat(this.fileFormat);

    readonly fileFormatSettings = this.boundFileFormat.settings;

    readonly exportsDecimalsAsStored = this.boundFileFormat.exportsDecimalsAsStored;

    private readonly previewShape = computed(() => {
        const stored = this.storedColumnConfig();
        return stored === null ? deliveredQuickExportShape : storedExportShape(stored);
    });

    readonly previewContent = computed<FilePreviewContent>(() =>
        previewContent(
            this.selectedFormat().outputFormat,
            {...this.previewShape(), fileFormat: this.fileFormatSettings()},
            (key, application, module) => this.translateService.translate(key, application, module),
        ),
    );

    readonly maxDate = DateTime.now().startOf('day');

    readonly selectedDate = new FormControl<DateTime | null>(this.maxDate, {
        validators: [Validators.required, AppValidators.maxDate(this.maxDate)],
    });

    readonly form: ExportForm = this.formBuilder.group({
        name: this.formBuilder.control<string | null>(null, [
            AppValidators.noEmptyStringValidator,
            Validators.maxLength(maxSavedExportNameLength),
        ]),
        timeZone: this.formBuilder.control<string>(browserTimeZone(), [Validators.required]),
        schedule: exportScheduleGroup(this.formBuilder, {
            ...defaultExportScheduleValue,
            startDate: this.maxDate,
        }),
    });

    private readonly isDateValid = toSignal(
        this.selectedDate.statusChanges.pipe(map(status => status === 'VALID')),
        {initialValue: this.selectedDate.valid},
    );

    private readonly selectedDateValue = toSignal(this.selectedDate.valueChanges, {
        initialValue: this.selectedDate.value,
    });

    private readonly nameValue = toSignal(this.form.controls.name.valueChanges, {
        initialValue: this.form.controls.name.value,
    });

    private readonly defaultName = defaultExportName(() =>
        this.translateService.translate('quickexports_export_default_name', 'online', 'export'),
    );

    readonly previewFileName = computed(() => {
        const date = this.selectedDateValue();
        if (date === null) {
            return null;
        }
        const {outputFormat} = this.selectedFormat();
        const name = this.nameValue();
        return this.storedColumnConfig() === null || name === null || name.trim() === ''
            ? meterValuesReportFileName(date, outputFormat)
            : savedExportFileName(name, date, outputFormat);
    });

    readonly isExportDisabled = computed(
        () =>
            this.isExporting() ||
            !this.isDateValid() ||
            this.boundFileFormat.status() === 'INVALID',
    );

    private readonly readingDateStatus = controlStepStatus(this.selectedDate);

    private readonly nameStatus = controlStepStatus(this.form.controls.name);

    private readonly fileFormatStatus = controlStepStatus(this.fileFormat);

    private readonly scheduleStatus = controlStepStatus(this.form.controls.schedule);

    readonly sftpConnectionRemoval = sftpConnectionRemoval(this.form.controls.schedule);

    private readonly scheduleValue = scheduleFormValue(computed(() => this.form.controls.schedule));

    readonly exportLimits = computed(() =>
        sftpConnectionRemovedLimits(this.sftpConnectionRemoval.removed(), this.scheduleValue()),
    );

    readonly stepStatuses = computed(
        () =>
            new Map<FlowSlot, StepStatus>([
                ['readingDate', this.readingDateStatus()],
                ['name', this.nameStatus()],
                ['format', this.fileFormatStatus()],
                ['schedule', this.scheduleStatus()],
            ]),
    );

    constructor() {
        effect(() => {
            const defaultName = this.defaultName();
            if (defaultName !== '' && this.form.controls.name.value === null) {
                this.form.controls.name.setValue(defaultName);
            }
        });

        // Name and schedule only exist on the schedule path; disabled they stay out of validity
        // while the specific-date path runs.
        effect(() => {
            if (this.selectedMode() === 'schedule') {
                this.form.controls.name.enable();
                if (this.scheduleGroupActive()) {
                    this.form.controls.schedule.enable();
                } else {
                    this.form.controls.schedule.disable();
                }
            } else {
                this.form.controls.name.disable();
                this.form.controls.schedule.disable();
            }
        });
    }

    selectMode(mode: QuickExportMode): void {
        if (this.selectedMode() === mode) {
            return;
        }
        this.selectedMode.set(mode);
        this.selectedFormat.set(defaultFormatForMode(mode));
    }

    selectModeById(id: string): void {
        const mode = this.modeOptions().find(option => option.id === id);
        if (mode !== undefined) {
            this.selectMode(mode.id);
        }
    }

    resetFileFormatStep(): void {
        resetExportFileFormat(this.fileFormat);
    }

    selectFormatById(id: string): void {
        const format = this.formatOptions().find(option => option.id === id);
        if (format !== undefined) {
            this.selectedFormat.set(format);
        }
    }

    hasUnsavedChanges(): boolean {
        return this.form.dirty || this.selectedDate.dirty;
    }

    buildTestRequest(): SavedExportTestRequest {
        const {outputFormat} = this.selectedFormat();
        const {name, schedule, timeZone} = this.form.getRawValue();
        return {
            outputFormat,
            name: name ?? '',
            exportType: 'QUICK',
            ...savedExportScheduleFields(true, schedule),
            timeZone,
            includeDismountedMeters: false,
            ...this.fileFormatSettings(),
            readingResolution: null,
            daysBackInTime: null,
            ...this.storedColumnConfig(),
        };
    }

    export(): void {
        const date = this.selectedDate.value;
        if (this.isExportDisabled() || date == null) {
            return;
        }
        this.isExporting.set(true);

        this.store
            .select(selectUser)
            .pipe(
                filter(state => state.status === 'success'),
                map(state => state.user.language ?? 'en'),
                take(1),
                switchMap(language =>
                    this.meterValuesReportService.downloadMeterValuesReport(
                        date,
                        language,
                        this.selectedFormat().outputFormat,
                        this.form.controls.timeZone.value,
                        this.fileFormatSettings(),
                    ),
                ),
                finalize(() => this.isExporting.set(false)),
            )
            .subscribe({
                next: () => {
                    this.selectedDate.markAsPristine();
                },
                error: () => {
                    // HTTP interceptor surfaces the generic error toast
                },
            });
    }

    saveSchedule(): void {
        const {outputFormat} = this.selectedFormat();
        if (this.isSaving()) {
            return;
        }
        const {name, schedule, timeZone} = this.form.getRawValue();
        if (this.form.invalid || name === null) {
            this.form.markAllAsTouched();
            return;
        }
        this.isSaving.set(true);

        this.savedExportsService
            .createSavedExport({
                outputFormat,
                name,
                exportType: 'QUICK',
                ...savedExportScheduleFields(true, schedule),
                timeZone,
                includeDismountedMeters: false,
                ...this.fileFormatSettings(),
                readingResolution: null,
                daysBackInTime: null,
            })
            .pipe(finalize(() => this.isSaving.set(false)))
            .subscribe({
                next: () => {
                    this.form.markAsPristine();
                    this.selectedDate.markAsPristine();
                    this.toastrService.success(
                        this.translateService.translate(
                            'savedexport_create_success',
                            'online',
                            'export',
                        ),
                    );
                    void this.router.navigate(['admin', 'export', 'saved-exports']);
                },
                error: () => {
                    // HTTP interceptor surfaces the generic error toast
                },
            });
    }
}

// One providers entry for every context that hosts the shared steps (wizard + edit modal), so the
// service and the EXPORT_FORM always ship together.
export const quickExportsFormProviders: Provider[] = [
    QuickExportsFormService,
    {provide: EXPORT_FORM, useFactory: (): ExportForm => inject(QuickExportsFormService).form},
    ...exportTestProviders(QuickExportsFormService),
];
